// @ts-nocheck
import { getApiClient, browserCacheBuster } from '@/lib/api/api-client';
import type { Exhibition } from '@/types';
import { logger } from '@/lib/utils/logger';
import { getCacheDuration } from '@/lib/config/cache.config';

const DEBUG = process.env.NODE_ENV === 'development';
import type {
  BaseQueryParams,
  PaginatedResponse,
  CatalogItem,
  FindInstallationsParams,
  WhereFilter,
} from './catalog-types';
import { getSortValue } from './catalog-types';
import { CacheService } from '../cache';
import { exhibitionTransformer } from '@/lib/transformers/exhibitions.transformer';
import { handleApiError, success, type ApiResult } from '@/lib/utils/errorHandler';
export function parseSearchParams(params: URLSearchParams): Partial<BaseQueryParams> {
  return {
    page: params.get('page') ? parseInt(params.get('page')!) : undefined,
    limit: params.get('limit') ? parseInt(params.get('limit')!) : 10,
    query: params.get('query') || undefined,
    depth: params.get('depth') ? parseInt(params.get('depth')!) : undefined,
    sort: params.get('sort') || undefined,
  };
}

/**
 * Transform Exhibition to CatalogItem
 */
export function exhibitionToCatalogItem(exhibition: Exhibition): CatalogItem {
  return exhibitionTransformer.toCatalogItem(exhibition);
}

/**
 * API service for Exhibitions
 */
export class ExhibitionsAPI {
  static apiClient = getApiClient();
  private static CACHE_DURATION = getCacheDuration('exhibitions');

  /**
   * Get exhibition list
   */
  static async findExhibitions(
    params: FindInstallationsParams & BaseQueryParams
  ): Promise<ApiResult<PaginatedResponse<CatalogItem>>> {
    const { page = 1, limit = 10, query, sort, years, ...rest } = params;

    const sortValue = getSortValue(sort, { dateField: 'duration.startDate' });

    // Build where query for Payload CMS v3
    const where: WhereFilter = {
      // Only show featured exhibitions (not history)
      featured: { equals: true },
    };

    // Search query
    if (query) {
      where.title = {
        contains: query,
      };
    }

    // Year filter - use duration.startDate field for Exhibition
    if (years && years.length > 0) {
      try {
        // Build OR query for multiple years
        const yearConditions = years.map(year => {
          const startDate = new Date(Number(year), 0, 1).toISOString();
          const endDate = new Date(Number(year), 11, 31, 23, 59, 59).toISOString();

          return {
            or: [
              // Exhibitions with start date within this year
              {
                and: [
                  { 'duration.startDate': { greater_than_equal: startDate } },
                  { 'duration.startDate': { less_than_equal: endDate } },
                ],
              },
              // Exhibitions with end date within this year
              {
                and: [
                  { 'duration.endDate': { greater_than_equal: startDate } },
                  { 'duration.endDate': { less_than_equal: endDate } },
                ],
              },
              // Exhibitions spanning this year (start before, end after)
              {
                and: [
                  { 'duration.startDate': { less_than_equal: startDate } },
                  { 'duration.endDate': { greater_than_equal: endDate } },
                ],
              },
            ],
          };
        });

        // Combine year filters with OR
        if (yearConditions.length === 1) {
          // Single year selected, add directly
          Object.assign(where, yearConditions[0]);
        } else {
          // Multiple years selected, combine with OR
          where.or = yearConditions;
        }
      } catch (error) {
        // Log error but continue with other filters
        logger.error('Error processing years filter:', error);
      }
    }

    // Additional where parameters
    if (rest.where) {
      Object.assign(where, rest.where);
    }

    if (DEBUG) {
      logger.warn('Exhibitions API where query:', where);
    }

    try {

      const response = await ExhibitionsAPI.apiClient.collections.exhibitions.find({
        page,
        limit,
        where,
        depth: params.depth || 2,
        // TODO: type properly - getSortValue returns dynamic strings not in payload-rest-client's keyof DOC union
        sort: sortValue as never,
        // Prevent caching
        ...browserCacheBuster(),
      });

      // Transform API response to Exhibition type
      const result = response as PaginatedResponse<Exhibition>;

      // Check totalPages and page values
      if (result.totalDocs && result.limit) {
        const totalPages = Math.ceil(result.totalDocs / result.limit);
        result.totalPages = totalPages;

        // If current page is less than total pages, there are more pages
        result.hasNextPage = result.page < totalPages;

        // Process results before type conversion
        const catalogItems = result.docs.map(exhibitionToCatalogItem);

        if (DEBUG) {
          logger.warn(
            `API response calculated: Page ${result.page}/${totalPages}, hasNextPage: ${result.hasNextPage}, totalDocs: ${result.totalDocs}, limit: ${result.limit}`
          );
        }

        // Return result as new object to avoid type mismatch
        return success({
          ...result,
          docs: catalogItems,
        } as PaginatedResponse<CatalogItem>);
      } else {
        // Default to false if totalDocs or limit is missing
        result.hasNextPage = false;
        if (DEBUG) {
          logger.warn(`API response calculated: hasNextPage: false (missing totalDocs or limit)`);
        }

        // Convert empty docs array to CatalogItem array
        return success({
          ...result,
          docs: [],
        } as PaginatedResponse<CatalogItem>);
      }
    } catch (error) {
      return handleApiError('findExhibitions', error);
    }
  }

  /**
   * Get exhibition detail by slug
   */
  static async findExhibitionBySlug(slug: string, skipCache: boolean = false): Promise<ApiResult<Exhibition>> {
    try {
      const cacheKey = await CacheService.scopedKey(`exhibition.service_${slug}`);
      // Check cache (if skipCache is false)
      if (!skipCache) {
        const cachedData = CacheService.getFromCache(cacheKey);
        if (cachedData) {
          return success(cachedData as Exhibition);
        }
      }

      const response = await ExhibitionsAPI.apiClient.collections.exhibitions.find({
        where: {
          slug: {
            equals: slug,
          },
        },
        depth: 2,
        limit: 1,
      });

      if (response.docs.length === 0) {
        return handleApiError('findExhibitionBySlug', new Error('Exhibition not found'));
      }
      // Write to cache
      CacheService.addToCache(cacheKey, response.docs[0], this.CACHE_DURATION);
      return success(response.docs[0]);
    } catch (error) {
      return handleApiError('findExhibitionBySlug', error);
    }
  }

  /**
   * Get exhibition history (displayMode: 'history')
   * Returns exhibitions grouped by type (Solo, Group, etc.)
   */
  static async findExhibitionHistory(): Promise<ApiResult<Record<string, Exhibition[]>>> {
    try {
      const cacheKey = await CacheService.scopedKey('exhibition.history');
      const cachedData = CacheService.getFromCache(cacheKey);
      if (cachedData) {
        return success(cachedData as Record<string, Exhibition[]>);
      }

      const response = await ExhibitionsAPI.apiClient.collections.exhibitions.find({
        where: {
          featured: {
            equals: false,
          },
        },
        depth: 2,
        limit: 100,
        sort: '-duration.startDate',
      });

      // Group by type.title
      const grouped: Record<string, Exhibition[]> = {};

      for (const exhibition of response.docs) {
        const typeTitle =
          typeof exhibition.type === 'object' && exhibition.type?.title
            ? exhibition.type.title
            : 'Other';

        if (!grouped[typeTitle]) {
          grouped[typeTitle] = [];
        }
        grouped[typeTitle].push(exhibition);
      }

      // Cache for 5 minutes
      CacheService.addToCache(cacheKey, grouped, 300);

      return success(grouped);
    } catch (error) {
      return handleApiError('findExhibitionHistory', error);
    }
  }
}
